import type { MoneybirdInvoice } from "@repo/api";
import type { ColumnDef } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { Permissions } from "@repo/database";
import { formatIntToCurrency } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import { Download, Paperclip } from "lucide-react";
import { useTranslation } from "react-i18next";

import { SettingsHeader } from "~/components/layout/settings-header";
import { DataTable } from "~/components/miscellaneous/data-table";
import { DataTableColumnHeader } from "~/components/miscellaneous/data-table-column-header";
import { Button } from "~/components/ui/button";
import { api } from "~/lib/api";
import { usePermissionManager } from "~/managers/use-permission-manager";
import { usePermissionStore } from "~/stores/use-permission-store";

export const Route = createFileRoute(
  "/_main/settings/_layout/account/invoices",
)({
  component: InvoicesPage,
});

function InvoicesPage() {
  const { t } = useTranslation("common");
  const { data } = api.billing.getInvoices.useQuery();
  const { currentEmployee } = usePermissionStore();
  const { hasPermission } = usePermissionManager();

  const [permission, setPermission] = useState(false);

  const { mutate: downloadAttachment } =
    api.billing.downloadAttachment.useMutation();

  const [selectedInvoice, setSelectedInvoice] = useState<
    MoneybirdInvoice | undefined
  >();

  const { data: downloadUrl } = api.billing.downloadInvoice.useQuery(
    selectedInvoice?.id,
    {
      enabled: !!selectedInvoice,
    },
  );

  const columns: ColumnDef<MoneybirdInvoice>[] = [
    {
      id: t("Billing.Invoices.number"),
      accessorKey: "invoiceId",
      filterFn: "includesString",
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("Billing.Invoices.number")}
        />
      ),
      cell: ({ row: { original: invoice } }) => {
        return <p>{invoice.invoiceId}</p>;
      },
    },
    {
      id: t("Billing.Invoices.total_amount"),
      accessorKey: "totalAmount",
      filterFn: "includesString",
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("Billing.Invoices.total_amount")}
        />
      ),
      cell: ({ row: { original: invoice } }) => {
        return <p>{formatIntToCurrency(invoice.totalAmount)}</p>;
      },
    },
    {
      id: t("Billing.Invoices.date"),
      accessorKey: "createdAt",
      filterFn: "includesString",
      sortingFn: (rowA, rowB) => {
        return (
          rowA.original.createdAt.getTime() - rowB.original.createdAt.getTime()
        );
      },
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("Billing.Invoices.date")}
        />
      ),
      cell: ({ row: { original: invoice } }) => {
        return <p>{format(invoice.createdAt, "P")}</p>;
      },
    },
    {
      id: "Options",
      enableSorting: false,
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader
          title="Options"
          className="justify-end"
          column={column}
        />
      ),
      cell: ({ row: { original: invoice } }) => {
        return (
          <div className="flex justify-end">
            {invoice.attachments.length > 0 && (
              <Button
                onClick={() => {
                  downloadAttachment(
                    {
                      invoiceId: invoice.id,
                      attachmentId: invoice.attachments[0].id,
                    },
                    {
                      onSuccess(url) {
                        window.open(url, "_blank");
                      },
                    },
                  );
                }}
                className="p-2"
                variant="ghost"
              >
                <Paperclip size={20} />
              </Button>
            )}
            <Button
              className="p-2"
              onClick={() => {
                setSelectedInvoice(invoice);
              }}
              variant="ghost"
            >
              <Download size={20} />
            </Button>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    if (downloadUrl && selectedInvoice) {
      // Create a temporary URL for the blob
      const byteString = atob(downloadUrl.base64PDF.split(",")[1]); // Remove the `data:application/pdf;base64,` prefix
      const byteArray = new Uint8Array(
        Array.from(byteString).map((char) => char.charCodeAt(0)),
      );
      const blob = new Blob([byteArray], { type: "application/pdf" });

      const url = URL.createObjectURL(blob);

      // Create an anchor element and trigger a download
      const a = document.createElement("a");
      a.href = url;
      a.download = `Factuur-${selectedInvoice.invoiceId}.pdf`; // Specify the file name
      document.body.appendChild(a); // Append to the DOM temporarily
      a.click(); // Trigger the download
      a.remove(); // Remove the element

      // Release the temporary URL
      URL.revokeObjectURL(url);
      setSelectedInvoice(undefined);
    }
  }, [downloadUrl, selectedInvoice]);

  useEffect(() => {
    if (!currentEmployee) {
      setPermission(false);
    }
    hasPermission({
      requiredPermission: Permissions.view_billing,
      label: t("Permissions.view_billing_label"),
    })
      .then((result) => {
        setPermission(result.permission);
      })
      .catch((e) => console.error(e));
  }, [hasPermission, currentEmployee, t]);

  if (!permission) return null;
  return (
    <>
      <SettingsHeader title={t("Billing.invoices_page_title")} />
      <DataTable
        defaultSorting={[{ id: t("Billing.Invoices.date"), desc: true }]}
        data={data ?? []}
        columns={columns}
      />
    </>
  );
}
